/**
 * The one way a descriptor's copy becomes text on screen.
 *
 * Every label the backend names — a column header, a stat card's title, that
 * card's tooltip — arrives the same way: a translation key, a literal fallback
 * for when the catalogue has no entry, and the tenant's vocabulary to
 * interpolate. Resolving all three through here keeps cards and columns from
 * drifting apart, which is what happened when each surface decided for itself
 * which terms a string was allowed to name.
 *
 * The vocabulary is passed whole and keyed by the field names the platform
 * already uses (`site_term_plural`, `relay_load_term_plural`, …), so copy names
 * the nouns it wants — `Static {{relay_load_term_plural}}` — and a string can
 * name two of them, or none, without anything here changing. Pointing a
 * customer type at a different key is therefore the whole of what it takes to
 * reword a number for them.
 *
 * Contains:
 * - resolveDescriptorLabel — (key, fallback, terms) → the text to render
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * @param {?Function} translate - the i18n translator, `(key, fallback, vars)`.
 *   Absent outside an i18n provider, where the fallback is the whole answer.
 * @param {?string} labelKey - the descriptor's translation key.
 * @param {string} fallback - literal copy for a key the catalogue lacks.
 * @param {Object<string, string>} [terms] - tenant vocabulary, keyed by term
 *   field name. Handed to the translator whether the copy names any of it or
 *   not; an unused variable is not an error to i18next.
 * @returns {string}
 */
export function resolveDescriptorLabel(translate, labelKey, fallback, terms) {
  if (!translate) return fallback;
  return translate(labelKey, fallback, terms);
}
